import React from "react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Area, Customized,
} from "recharts";
import { ZoomOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { shortDate, usd, num } from "../lib/format";
import { timeAxis, priceAxisDecimals, axisWidth } from "../lib/chartAxis";
import Hint from "./Hint";
import useIsMobile from "../lib/useIsMobile";
import useZoomPan from "../lib/useZoomPan";

const SMA_COLORS = { 20: "#06b6d4", 50: "#f59e0b", 100: "#10b981", 200: "#7c3aed", 500: "#db2777" };
const UP = "#059669";
const DOWN = "#e11d48";

function smaSeries(data, period, key = "close") {
  return data.map((_, i) => {
    if (i < period - 1) return null;
    let s = 0;
    for (let j = i - period + 1; j <= i; j++) s += data[j][key];
    return +(s / period).toFixed(4);
  });
}

function heikinAshi(data) {
  const out = [];
  let prevO = null, prevC = null;
  for (const d of data) {
    const haClose = (d.open + d.high + d.low + d.close) / 4;
    const haOpen = prevO == null ? (d.open + d.close) / 2 : (prevO + prevC) / 2;
    const haHigh = Math.max(d.high, haOpen, haClose);
    const haLow = Math.min(d.low, haOpen, haClose);
    out.push({ ...d, open: haOpen, close: haClose, high: haHigh, low: haLow });
    prevO = haOpen; prevC = haClose;
  }
  return out;
}

function CandleTooltip({ active, payload, isCrypto, labelFormatter = shortDate }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  const o = { crypto: isCrypto };
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-soft px-3 py-2 text-xs tabular">
      <div className="font-semibold mb-1">{labelFormatter(d.date)}</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-slate-600">
        <span>O {num(d.open, 2, o)}</span><span>H {num(d.high, 2, o)}</span>
        <span>L {num(d.low, 2, o)}</span><span>C {num(d.close, 2, o)}</span>
      </div>
      {d.volume ? <div className="text-slate-400 mt-1">Vol {num(d.volume, 0)}</div> : null}
    </div>
  );
}

const CANDLE_TYPES = new Set(["candlestick", "heikin", "ohlc"]);

// Draws candles / OHLC bars as a single SVG layer using the real axis scales so
// each candle is centred exactly on its category (aligned with the cursor, grid
// and the volume bar underneath). Avoids recharts' grouped-bar offset.
function CandleLayer({ data, type, xAxisMap, yAxisMap }) {
  const xMap = xAxisMap && Object.values(xAxisMap)[0];
  const yMap = yAxisMap && yAxisMap.price;
  if (!xMap || !yMap) return null;
  const xs = xMap.scale, ys = yMap.scale;
  const band = xs.bandwidth ? xs.bandwidth() : 0;
  const cxOf = (d) => (xs(d) ?? 0) + band / 2;
  const bw = Math.max(2, Math.min(band * 0.6, 16));

  return (
    <g>
      {data.map((d, i) => {
        if (d.high == null || d.low == null || d.open == null || d.close == null) return null;
        const cx = cxOf(d.date);
        const yH = ys(d.high), yL = ys(d.low);
        const yO = ys(d.open), yC = ys(d.close);
        const up = d.close >= d.open;
        const color = up ? UP : DOWN;
        if (type === "ohlc") {
          const tick = Math.max(2, bw * 0.55);
          return (
            <g key={i}>
              <line x1={cx} x2={cx} y1={yH} y2={yL} stroke={color} strokeWidth={1.4} />
              <line x1={cx - tick} x2={cx} y1={yO} y2={yO} stroke={color} strokeWidth={1.4} />
              <line x1={cx} x2={cx + tick} y1={yC} y2={yC} stroke={color} strokeWidth={1.4} />
            </g>
          );
        }
        const bodyTop = Math.min(yO, yC);
        const bodyH = Math.max(1, Math.abs(yC - yO));
        return (
          <g key={i}>
            <line x1={cx} x2={cx} y1={yH} y2={yL} stroke={color} strokeWidth={1} />
            <rect x={cx - bw / 2} y={bodyTop} width={bw} height={bodyH} fill={color} rx={1} />
          </g>
        );
      })}
    </g>
  );
}

// `intraday` comes from the history response: short ranges (1D/1W/1M) are 5, 15 or
// 60-minute candles, and their labels need a time of day.
export default function PriceChart({ data, type = "line", smas = [50, 200], showVolume = true, isCrypto = false, intraday = false }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { containerRef, range, reset, zoomed } = useZoomPan(data || []);
  if (!data || data.length === 0) {
    return <div className="h-[280px] sm:h-[360px] flex items-center justify-center text-slate-400 text-sm">{t("instrument.noPriceHistory")}</div>;
  }
  const isCandle = CANDLE_TYPES.has(type);
  const base = type === "heikin" ? heikinAshi(data) : data;
  const smaVals = {};
  // SMAs use the same series that is drawn (HA-adjusted for heikin) so lines sit
  // consistently with the candles on screen.
  smas.forEach((p) => { smaVals[p] = smaSeries(base, p); });
  const prepared = base.map((d, i) => {
    const row = { ...d };
    smas.forEach((p) => { row[`sma${p}`] = smaVals[p][i]; });
    return row;
  });
  // Only the currently zoomed-in window is rendered, so price/volume scales
  // rescale to fill the chart the same way TradingView does when you zoom.
  const windowed = prepared.slice(range.start, range.end + 1);

  const min = Math.min(...windowed.map((d) => d.low ?? d.close));
  const max = Math.max(...windowed.map((d) => d.high ?? d.close));
  const pad = (max - min) * 0.08 || 1;
  const maxVol = Math.max(...windowed.map((d) => d.volume || 0)) || 1;
  const up = windowed[windowed.length - 1].close >= windowed[0].close;
  const stroke = up ? "#0052FF" : "#e11d48";

  const baseVal = data[0].close;
  const lo = min - pad, hi = max + pad;
  const offset = Math.max(0, Math.min(1, (hi - baseVal) / (hi - lo)));

  const activeSmas = smas.filter((p) => smaVals[p].some((v) => v != null));
  // Worked out on the zoomed window, so zooming a week down to a day switches the
  // axis from dates to times.
  const time = timeAxis(windowed, intraday);
  const yDec = priceAxisDecimals(lo, hi);
  // Crypto's 9-decimal formatting is for sub-cent coins; on Bitcoin's axis it printed
  // 78.905,123456789 into a 38px column. Only a sub-1 price needs it on the axis.
  const axisCrypto = isCrypto && hi < 1;
  const yFmt = (v) => num(v, yDec, { crypto: axisCrypto });
  const yWidth = axisWidth([yFmt(lo), yFmt(hi)], isMobile ? 10 : 11, isMobile ? 38 : 52);

  return (
    <div>
      <div className="text-xs text-slate-400 mb-2">{t("instrument.zoomHint")}</div>
      <div ref={containerRef} className="relative h-[280px] sm:h-[360px] touch-pan-y">
        {zoomed && (
          <button onClick={reset} data-testid="price-chart-reset-zoom"
            className="absolute top-1 right-1 z-10 inline-flex items-center gap-1 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-[11px] font-medium text-slate-600 border border-slate-200 shadow-sm hover:text-brand-600 hover:border-brand-200 transition-colors">
            <ZoomOut size={12} /> {t("instrument.resetZoom")}
          </button>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={windowed} margin={{ top: 10, right: isMobile ? 0 : 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="closeFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={stroke} stopOpacity={0.18} />
                <stop offset="100%" stopColor={stroke} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="baseFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#059669" stopOpacity={0.5} />
                <stop offset={offset} stopColor="#059669" stopOpacity={0.05} />
                <stop offset={offset} stopColor="#e11d48" stopOpacity={0.05} />
                <stop offset="100%" stopColor="#e11d48" stopOpacity={0.5} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" {...time.axis} tick={{ fontSize: isMobile ? 10 : 11, fill: "#94a3b8" }} minTickGap={isMobile ? 24 : 40} axisLine={false} tickLine={false} />
            <YAxis yAxisId="price" domain={[lo, hi]} tick={{ fontSize: isMobile ? 10 : 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={yWidth} tickFormatter={yFmt} />
            <YAxis yAxisId="vol" orientation="right" domain={[0, maxVol * 4]} hide />
            <Tooltip content={isCandle ? <CandleTooltip isCrypto={isCrypto} labelFormatter={time.tooltip.labelFormatter} /> : undefined}
              formatter={!isCandle ? (v, n) => [n === "volume" ? num(v, 0) : usd(v, { crypto: isCrypto }), n] : undefined}
              {...time.tooltip}
              contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />

            {showVolume && <Bar yAxisId="vol" dataKey="volume" fill="#dbeafe" isAnimationActive={false} />}

            {isCandle && <Customized component={(props) => <CandleLayer {...props} data={windowed} type={type} />} />}
            {type === "area" && <Area yAxisId="price" type="linear" dataKey="close" stroke={stroke} strokeWidth={2} fill="url(#closeFill)" isAnimationActive={false} />}
            {type === "baseline" && <Area yAxisId="price" type="linear" dataKey="close" stroke="#334155" strokeWidth={1.5} fill="url(#baseFill)" isAnimationActive={false} />}
            {type === "line" && <Line yAxisId="price" type="linear" dataKey="close" stroke={stroke} strokeWidth={2} dot={false} isAnimationActive={false} />}

            {activeSmas.map((p) => (
              <Line key={p} yAxisId="price" type="linear" dataKey={`sma${p}`} stroke={SMA_COLORS[p] || "#64748b"} strokeWidth={1.4} dot={false} name={`SMA ${p}`} isAnimationActive={false} connectNulls />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-2 text-xs text-slate-500">
        {activeSmas.map((p) => (
          <Hint key={p} term={`SMA ${p}`}>
            <span className="flex items-center gap-1.5"><span className="w-3 h-0.5" style={{ background: SMA_COLORS[p] }} /> SMA {p}</span>
          </Hint>
        ))}
        {showVolume && <Hint term="Volume"><span className="flex items-center gap-1.5"><span className="w-3 h-2 bg-blue-100" /> {t("instrument.volume")}</span></Hint>}
      </div>
    </div>
  );
}
